import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Paprika } from '../../../styles/theme';
import { BottomSheet } from '../../weekly/v2/primitives/BottomSheet';
import type { DietKind } from '../../../redux/mealPlanUISlice';
import { useTranslation } from 'react-i18next';

interface Props {
  visible: boolean;
  mealLabel: string;
  portions: number;
  kind: DietKind;
  /** Week default — shown in the reset link when the cell overrides it. */
  defaultPortions: number;
  hasPortionsOverride: boolean;
  onResetPortions: () => void;
  onPortionsChange: (n: number) => void;
  onKindChange: (k: DietKind) => void;
  onRemove: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

const DIET_OPTIONS: { key: DietKind; labelKey: string }[] = [
  { key: 'auto',  labelKey: 'mealConfig.auto' },
  { key: 'fish',  labelKey: 'mealConfig.fish' },
  { key: 'veg',   labelKey: 'mealConfig.veggie' },
  { key: 'meat',  labelKey: 'mealConfig.meat' },
];

export function CellSheet({ visible, mealLabel, portions, kind, defaultPortions, hasPortionsOverride, onResetPortions, onPortionsChange, onKindChange, onRemove, onConfirm, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="72%">
      <View style={styles.content}>
        <Text style={styles.heading}>{mealLabel}</Text>

        <Text style={styles.sectionLabel}>{t('thisWeek.portionsLabel')}</Text>
        <View style={styles.stepper}>
          <Pressable style={[styles.stepBtn, portions <= 1 && styles.stepBtnDisabled]} onPress={() => onPortionsChange(Math.max(1, portions - 1))}>
            <Text style={styles.stepBtnText}>−</Text>
          </Pressable>
          <Text style={styles.stepValue}>{portions}</Text>
          <Pressable style={[styles.stepBtn, portions >= 12 && styles.stepBtnDisabled]} onPress={() => onPortionsChange(Math.min(12, portions + 1))}>
            <Text style={styles.stepBtnText}>+</Text>
          </Pressable>
        </View>
        {hasPortionsOverride ? (
          <Pressable onPress={onResetPortions} style={styles.resetLink}>
            <Text style={styles.resetLinkText}>{t('thisWeek.resetToWeekDefault', { count: defaultPortions })}</Text>
          </Pressable>
        ) : (
          <Text style={styles.defaultHint}>{t('mealConfig.weekDefault')}</Text>
        )}

        <Text style={styles.sectionLabel}>{t('mealConfig.mealType')}</Text>
        <View style={styles.dietGrid}>
          {DIET_OPTIONS.map((opt) => (
            <Pressable
              key={opt.key}
              style={[styles.dietOption, kind === opt.key && styles.dietOptionActive]}
              onPress={() => onKindChange(opt.key)}
            >
              <Text style={[styles.dietOptionText, kind === opt.key && styles.dietOptionTextActive]}>
                {t(opt.labelKey)}
              </Text>
            </Pressable>
          ))}
        </View>

        <Pressable style={styles.removeBtn} onPress={onRemove}>
          <Text style={styles.removeBtnText}>{t('mealConfig.removeThisMeal')}</Text>
        </Pressable>

        <Pressable style={styles.confirmBtn} onPress={onConfirm}>
          <Text style={styles.confirmBtnText}>{t('common.ok')}</Text>
        </Pressable>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 20,
    paddingTop: 8,
    gap: 16,
  },
  heading: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: Paprika.textPrimary,
    textAlign: 'center',
  },
  sectionLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textMuted,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    marginBottom: 4,
  },
  stepBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepBtnDisabled: {
    opacity: 0.35,
  },
  stepBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 22,
    color: Paprika.textPrimary,
    lineHeight: 24,
  },
  stepValue: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 40,
    color: Paprika.textPrimary,
    minWidth: 50,
    textAlign: 'center',
    lineHeight: 48,
  },
  dietGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dietOption: {
    flex: 1,
    minWidth: '44%' as any,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLow,
  },
  dietOptionActive: {
    borderColor: Paprika.brandSoft,
    backgroundColor: Paprika.brandTint,
  },
  dietOptionText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  dietOptionTextActive: {
    color: Paprika.primary,
  },
  resetLink: {
    alignItems: 'center',
    marginTop: -8,
  },
  resetLinkText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.primary,
  },
  defaultHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
    textAlign: 'center',
    marginTop: -8,
  },
  removeBtn: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  removeBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
    textDecorationLine: 'underline',
  },
  confirmBtn: {
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 15,
    alignItems: 'center',
    marginBottom: 8,
  },
  confirmBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: '#fff',
  },
});
